import { inject, Injectable } from '@angular/core';

import { ApplicationUserService } from '../application-user.services/application-user.service';
import { DomainProfileService } from '../domain-profile-services/domain-profile.service';
import { IResponseData } from '../../models/response-data';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { domainUserData, DomainUserData } from 'src/app/features/domain-user/models/domain-user-data';
import { projectMemberData, ProjectMemberData } from 'src/app/features/project-member/models/project-member-data';
import { UserData } from 'src/app/features/user/models/user-data';

@Injectable({
    providedIn: 'root'
})
export class ResponseStateService {
    private readonly __applicationUserService = injectRequired(ApplicationUserService);
    private readonly __domainProfileService   = injectRequired(DomainProfileService);

    // suppressDomainScope: deze response's domainUserDto/projectMemberDto NIET naar de gedeelde,
    // sessie-brede ApplicationUserService.domainUserData/projectMemberData schrijven (zie
    // API_SUPPRESS_DOMAIN_SCOPE_HEADER in api.service.ts) — voor responses waarvan de domain-scope
    // uitsluitend voor DIT bezoek/deze claims-tagging bedoeld is, niet als "huidig bezochte domain"
    // voor andere, gelijktijdig gemounte pagina's die op dat gedeelde veld terugvallen (zie
    // ActivatedRouteService.routeKeys). Claims/userData blijven wel gewoon toegepast, met de dGuid/
    // oGuid van DEZE response expliciet meegegeven i.p.v. via het (dan niet-bijgewerkte) gedeelde veld.
    apply(body: IResponseData | null | undefined, options?: { suppressDomainScope?: boolean }): void {
        const __userClaims = body?.claimsDto;
        if (!__userClaims) {
            // Lijst-responses dragen geen claims, wel de owner-set. Alleen die verwerken: domain-/userdata
            // niet aanraken (een lijst-antwoord zou die anders wissen). Scope = het huidige domein.
            this.__applicationUserService.ownerClaims_set(body?.ownerClaims);
            return;
        }

        const __domainUserDto  = body.domainUserDto;
        const __projectMemberDto = body.projectMemberDto;
        const __userDto        = body.userDto;

        const __domainUserData = DomainUserData.create(__domainUserDto);
        const __projectMemberData = ProjectMemberData.create(__projectMemberDto);
        const __userData = UserData.create(__userDto);

        if (!options?.suppressDomainScope) {
            const __domainData = __domainUserData?.[domainUserData.domainData] ?? null;
            this.__domainProfileService.domainProfile_set(__domainData);

            this.__applicationUserService.domainUserData  = __domainUserData  ?? null;
            this.__applicationUserService.projectMemberData = __projectMemberData ?? null;
        }
        // Expliciete User uit de response (nodig buiten domein/project, bv. Home); binnen domein/project
        // leunt UserGuid anders op domainUser.userGuid / projectMember.userDto.
        this.__applicationUserService.userData = __userData ?? null;
        this.__applicationUserService.claims_new(
            __userClaims,
            __domainUserData?.[domainUserData.guid] ?? null,
            __projectMemberData?.[projectMemberData.guid] ?? null,
        );
        // Zelfde scope als de claims van deze response: de owner-set is gefilterd op déze DomainUser.
        this.__applicationUserService.ownerClaims_set(body?.ownerClaims, __domainUserData?.[domainUserData.guid] ?? null);
    }
}
